import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'
import { resolveTriggerListHistoryId } from 'utils/resolveSiteWorkflowIds'
import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Tooltip from 'components/Tooltip'
import InfoIcon from 'assets/icons/info-icon'
import Accordion from 'components/Accordion'
import Modal from 'components/Modal'
import RemoveCriterionCModal from './removeCriterionC'
import { SITE_STATUS_DRAFT_ID, SITE_STATUS_NOMINATED_ID } from 'constants/siteStatuses'
import { deleteCriterionC, requestGetCriterionCs, requestUpdateCriterionC } from 'api/criterionC'
import { AssessmentPageSubTittle, AssessmentPageSubTittleIconWrapper, AssessmentPageSubTittleWrapper, AssessmentPageTittle, AssessmentPageTittleWrapper } from '../../style'
import AccordionContent from './accordionContent'
import AccordionHeader from './accordionHeader'
import { EcosystemListTitle, EcosystemListTitleWrapper } from '../ecosystemData/style'
import { getCriterionCId, normalizeCriterionCItems } from 'utils/criterionCHelpers'
import { useOverallKbaCriteriaRefresh } from 'flow/siteProposal/pages/assessment/context/OverallKbaCriteriaRefreshContext'

const defaultCriterionC = {
    ecoregion_id: null,
    ecoregion_c_sites_count: '',
    year: '',
    evidence_low_human_impact: '',
    evidence_intact_ecology: '',
    trigger_reason_id: [],
    trigger_reason_text: '',
    reassessment_flag: false,
    EcoRegion: {
        id: null,
        name: '',
    }
}

const tooltipXOffset = 0
const tooltipYOffset = 16
const CriterionCList = ({ site }) => {
    const params = useParams()
    const { refetchCriteria } = useOverallKbaCriteriaRefresh()
    const [criterionCs, setCriterionCs] = useState([])
    const [isRemoveCriterionCModalOpen, setIsRemoveCriterionCModalOpen] = useState(false)
    const [willBeDeletedCriterionC, setWillBeDeletedCriterionC] = useState({})

    async function getCriterionCs() {
        const data = await requestGetCriterionCs(site.id, resolveTriggerListHistoryId(site, params))
        setCriterionCs(normalizeCriterionCItems(data))
    }

    async function requestDeleteCriterionC(criterionId) {
        await deleteCriterionC(criterionId, params.siteHistoryId)
        await getCriterionCs()
        refetchCriteria()
    }

    const openRemoveCriterionCModal = (criterion) => {
        setIsRemoveCriterionCModalOpen(true)
        setWillBeDeletedCriterionC(criterion)
    }

    const updateCriterionC = async (data) => {
        const criterionCId = data.criterionCId
        if (!criterionCId) {
            console.error('updateCriterionC: criterionCId is required')
            return
        }
        const updatePayload = { ...data }
        delete updatePayload.criterionCId
        const dataCrit = criterionCs.find((item) => getCriterionCId(item) === criterionCId)
        let critCs

        if (!Array.isArray(dataCrit?.trigger_reason_id) || dataCrit.trigger_reason_id.length === 0) {
            critCs = await requestUpdateCriterionC(criterionCId, params.siteHistoryId, { ...updatePayload, trigger_reason_id: [1] })
        }

        if (Array.isArray(dataCrit?.trigger_reason_id) && dataCrit.trigger_reason_id.length > 0) {
            critCs = await requestUpdateCriterionC(criterionCId, params.siteHistoryId, updatePayload)
        }

        if (!Array.isArray(critCs)) return
        setCriterionCs(normalizeCriterionCItems(critCs))
        refetchCriteria()
    }

    useEffect(() => {
        const fetchCriterionCs = async () => {
            await getCriterionCs()
        }
        fetchCriterionCs()
    }, [])

    if (criterionCs.length === 0 && site.SiteStatusId !== SITE_STATUS_DRAFT_ID) {
        return null
    }

    const approvedCriterionCs = criterionCs.filter((item) => item?.is_approved_by_secretariat)
    const rejectedCriterionCs = criterionCs.filter((item) => !item?.is_approved_by_secretariat)

    const newCriterionCs = criterionCs.filter((item) => !item?.last_reassessment_date)
    const oldCriterionCs = criterionCs.filter((item) => item?.last_reassessment_date)

    return (
        <div>
            <Container>
                <Row>
                    <Col md={8} mdOffset={2}>
                        <AssessmentPageTittleWrapper>
                            <AssessmentPageTittle data-testid='legend-criterionC'>
                                <FormattedMessage id='criterionC' />
                            </AssessmentPageTittle>
                            <AssessmentPageSubTittleWrapper marginBottom='64px' data-testid='sub-legend-criterionC'>
                                <AssessmentPageSubTittle>
                                    <FormattedMessage id='criterionCSubTittle' />
                                </AssessmentPageSubTittle>
                                <Tooltip
                                    style={{ zIndex: 1000 }}
                                    placement='bottom'
                                    trigger={['hover']}
                                    align={{ offset: [tooltipXOffset, tooltipYOffset] }}
                                    overlay={{ label: 'ecoRegion', infoTooltip: 'ecoRegionHint' }}
                                    hyper={'https://www.keybiodiversityareas.org/working-with-kbas/programme/regional-focal-points'}
                                    shortLink={'www.keybiodiversityareas.org'}
                                    type='big'
                                >
                                    <AssessmentPageSubTittleIconWrapper>
                                        <InfoIcon />
                                    </AssessmentPageSubTittleIconWrapper>
                                </Tooltip>
                            </AssessmentPageSubTittleWrapper>
                        </AssessmentPageTittleWrapper>
                    </Col>
                </Row>
                <Row>
                    <Col md={8} mdOffset={2} dataTest='criterionC-group'>
                        {criterionCs.length === 0 ? (
                            <AccordionContent item={defaultCriterionC} updateCriterionC={updateCriterionC} siteId={site.id} defaultPage={true} site={site} />
                        ) : (
                            <>
                                {site.SiteStatusId === SITE_STATUS_NOMINATED_ID ? (
                                    <>
                                        {approvedCriterionCs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper>
                                                            <EcosystemListTitle marginTop='0'>
                                                                <FormattedMessage
                                                                    id='approvedTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={approvedCriterionCs}
                                                    contentProps={{ updateCriterionC, site }}
                                                    headerProps={{ openRemoveCriterionCModal, getCriterionCs, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                    initialActive={null}
                                                />
                                            </>
                                        )}
                                        {rejectedCriterionCs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper>
                                                            <EcosystemListTitle marginTop='48px'>
                                                                <FormattedMessage
                                                                    id='rejectedTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={rejectedCriterionCs}
                                                    contentProps={{ updateCriterionC, site }}
                                                    headerProps={{ openRemoveCriterionCModal, getCriterionCs, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                    initialActive={null}
                                                />
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {oldCriterionCs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper>
                                                            <EcosystemListTitle>
                                                                <FormattedMessage id='existingTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={oldCriterionCs}
                                                    contentProps={{ updateCriterionC, site }}
                                                    headerProps={{ openRemoveCriterionCModal, getCriterionCs, oldTriggers: true, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                    initialActive={null}
                                                />
                                            </>
                                        )}
                                        {newCriterionCs.length > 0 && (
                                            <>
                                                <Row>
                                                    <Col md={8} mdOffset={2}>
                                                        <EcosystemListTitleWrapper addMarginTop={true}>
                                                            <EcosystemListTitle>
                                                                <FormattedMessage id='newTriggers' />
                                                            </EcosystemListTitle>
                                                        </EcosystemListTitleWrapper>
                                                    </Col>
                                                </Row>
                                                <Accordion
                                                    items={newCriterionCs}
                                                    contentProps={{ updateCriterionC, site }}
                                                    headerProps={{ openRemoveCriterionCModal, getCriterionCs, newTriggers: true, site }}
                                                    contentComponent={AccordionContent}
                                                    headerComponent={AccordionHeader}
                                                />
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Col>
                </Row>
            </Container>
            <Modal
                width={'416px'}
                isOpen={isRemoveCriterionCModalOpen}
                toggleModal={setIsRemoveCriterionCModalOpen}
                modalTitleId='removeCriterionC'
                bottomBorder={true}
            >
                <RemoveCriterionCModal
                    item={willBeDeletedCriterionC}
                    closeModal={() => {
                        setIsRemoveCriterionCModalOpen(false)
                    }}
                    confirmRemove={async() => {
                        await requestDeleteCriterionC(willBeDeletedCriterionC.site_ecointegrity_history_id || willBeDeletedCriterionC.site_ecointegrity_id)
                        setIsRemoveCriterionCModalOpen(false)
                    }}
                />
            </Modal>
        </div>
    )
}

export default CriterionCList

const criterionCItemPropType = PropTypes.shape({
  site_ecointegrity_history_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  site_ecointegrity_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))
})

CriterionCList.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    SiteStatusId: PropTypes.number.isRequired,
    sitehistoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    criterionCs: PropTypes.arrayOf(criterionCItemPropType)
  }).isRequired
}